import React, { useEffect, useRef, useState } from 'react';
import { type DonneesReleve } from '../../services/relevesApi';
import { documentA4, imprimerHtmlDansIframe } from '../../utils/impressionA4';
import apiClient from '../../config/api.config';
import { useGetInfoInstitutQuery } from '../../services/coreApi';

interface ImpressionPasse {
  id: number;
  type_releve: 'provisoire' | 'definitif';
  scope: string;
  scope_label: string;
  nombre_copies: number;
  numero_reference_externe: string;
  date_impression: string | null;
  imprime_par_nom: string;
  imprime_par_role: string;
  concerne: string;
}
interface HistoriqueResponse {
  matricule: string;
  etudiant_nom: string;
  classe: string;
  deja_imprime_definitif: boolean;
  nombre_impressions_definitif: number;
  nombre_evenements_definitif: number;
  nombre_impressions_provisoire: number;
  derniere_impression_definitif: string | null;
  impressions: ImpressionPasse[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getMention(moy: number | null): string {
  if (moy === null) return '—';
  if (moy >= 16) return 'Très Bien';
  if (moy >= 14) return 'Bien';
  if (moy >= 12) return 'Assez Bien';
  if (moy >= 10) return 'Passable';
  return 'Ajourné';
}

// Appréciation d'un module DD d'après sa note /20 (comble la colonne libérée
// par le retrait du formateur/provenance).
function apprecDD(n: number | null | undefined): string {
  // Barème fourni (Excel) : >=16 Très Bien, >=14 Bien, >=12 Assez Bien, sinon Passable.
  if (n === null || n === undefined) return '—';
  if (n >= 16) return 'Très Bien';
  if (n >= 14) return 'Bien';
  if (n >= 12) return 'Assez Bien';
  return 'Passable';
}

function fN(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toFixed(2).replace('.', ',');
}

function semestreCumulatif(niveau: string, sem: number): number {
  const off: Record<string, number> = { L1: 0, L2: 2, L3: 4, M1: 0, M2: 2 };
  return (off[niveau] ?? 0) + sem;
}

function ordinal(n: number): string {
  return n === 1 ? '1er' : `${n}e`;
}

// ─── Palette ─────────────────────────────────────────────────────────────────

const COLORS = {
  primary: '#1E3A8A',          // bleu profond institutionnel
  primaryLight: '#3B82F6',     // bleu clair
  primarySoft: '#EFF6FF',      // bleu très pâle (fond)
  accent: '#B45309',           // ocre/or pour bandeaux
  accentSoft: '#FEF3C7',       // jaune pâle
  validBg: '#D1FAE5',          // vert clair
  validText: '#065F46',
  invalidBg: '#FEE2E2',
  invalidText: '#991B1B',
  border: '#1F2937',           // gris foncé pour bordures
  borderLight: '#9CA3AF',      // gris moyen
  text: '#111827',
  textMuted: '#4B5563',
};

// Fix 2026-09-14 : le relevé est imprimé seul, sur une feuille A4 ajustée. window.print()
// sur la page de l'application coupait le bas des relevés chargés de matières (fenêtre
// d'aperçu fixe, à hauteur limitée) : résumé, décision et signature disparaissaient.
export async function imprimerRelevePage(page: HTMLElement, titre: string): Promise<void> {
  const clone = page.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('.rn-no-print').forEach((n) => n.remove());
  // Filigrane centré sur la feuille (et non sur la fenêtre)
  clone.querySelectorAll<HTMLElement>('.rn-filigrane').forEach((f) => { f.style.position = 'absolute'; });
  const html = documentA4({
    titre,
    css: `body { font-family: "Calibri", "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif; font-size: 11px; color: ${COLORS.text}; letter-spacing: 0.01em; }
      .feuille-a4 { padding: 0.35cm 0.9cm; }
      .feuille-a4 tr { break-inside: avoid; page-break-inside: avoid; }`,
    corps: `<div class="feuille-a4">${clone.innerHTML}</div>`,
  });
  await imprimerHtmlDansIframe(html, '.feuille-a4');
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface RelevePreviewProps {
  data: DonneesReleve;
  typeReleve: 'provisoire' | 'definitif';
  qrCodeDataUrl?: string | null;
  numeroReference?: string;
  onClose?: () => void;
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const RelevePreview: React.FC<RelevePreviewProps> = ({
  data, typeReleve, qrCodeDataUrl, numeroReference, onClose,
}) => {
  const { etudiant: e, annee_academique, semestre, ues, resultat_semestre: rs } = data;
  const dd = data.double_diplome || null;  // cursus Double Diplôme (semestre pair)
  // Relevé Double Diplôme : on garde le BLEU institutionnel (la distinction se
  // fait par le cycle « CURSUS DOUBLE DIPLÔME », la section DD et le logo du
  // partenaire ISPI dans l'en-tête).
  const C = COLORS;
  const estDD = (e.type_formation || '').toUpperCase() === 'DD'
    || (e.cycle || '').toUpperCase().includes('DOUBLE DIPL');
  const isProvisoire = typeReleve === 'provisoire';
  const mention = getMention(rs?.moyenne_semestre ?? null);
  const semCum = semestreCumulatif(e.niveau, semestre);
  const dateStr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

  // Fix 2026-08-28 : infos institut selon le déploiement (PN/BZ) — au lieu des
  // anciennes valeurs Pointe-Noire hardcodées, on lit /parametres/info-institut/
  // qui renvoie ville/adresse/tel/email/chef_scolarité selon institut_deployment.
  const { data: institut } = useGetInfoInstitutQuery();
  const infoTel = institut?.telephone || '(+242) 06 855 67 94 / 06 736 10 00 / 06 455 3800';
  const infoEmail = institut?.email || 'contact2i@institut2i.com';
  const infoAdresse = institut?.adresse_rue || '06, RUE LI-LELEMB BASE INDUSTRIELLE';
  const infoVille = (institut?.ville || 'POINTE-NOIRE').toUpperCase();
  const infoVilleCap = institut?.ville || 'Pointe-Noire';
  const infoAgrement = institut?.numero_agrement || "Agrément définitif de l'Etat S/N° 196 MES-CAB.DG-DAAC du 21/04/2021";
  const infoSite = institut?.site_web || 'www.institut2i.com';
  // Signataire du relevé. À Brazzaville : titre + nom du chef (laisser l'espace de
  // signature). À Pointe-Noire : titre seul (cachet nominatif). 2026-10-10.
  const isBZ = institut?.deployment === 'bz';
  // Un seul « Le » : le réglage institut peut déjà commencer par « Le » (le
  // placeholder le suggère), d'où « Le Le Chef… » si on préfixe à l'aveugle.
  const chefTitreBase = (institut?.chef_scolarite_titre || 'Chef de Service de la Scolarité et Examens').trim();
  const chefTitre = /^le\s/i.test(chefTitreBase) ? chefTitreBase : `Le ${chefTitreBase}`;
  const chefNom = institut?.chef_scolarite || '';
  const b = `1px solid ${COLORS.border}`;
  const bSoft = `1px solid ${COLORS.borderLight}`;

  // Historique d'impression : pour les définitifs, charger les impressions
  // précédentes afin d'alerter sur la délivrance d'un nouveau original.
  const [historique, setHistorique] = useState<HistoriqueResponse | null>(null);
  const [showHistoriqueDetails, setShowHistoriqueDetails] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const [impressionEnCours, setImpressionEnCours] = useState(false);
  const imprimer = async () => {
    if (!pageRef.current) return;
    setImpressionEnCours(true);
    try {
      await imprimerRelevePage(pageRef.current, `Releve_${e.matricule}_S${semCum}${isProvisoire ? '_provisoire' : ''}`);
    } catch {
      window.print();
    } finally {
      setImpressionEnCours(false);
    }
  };
  useEffect(() => {
    if (typeReleve !== 'definitif' || !e?.matricule) return;
    apiClient
      .get('/releves/verifier-impressions-etudiant/', {
        params: { matricule: e.matricule, semestre, annee_academique },
      })
      .then((r) => setHistorique(r.data))
      .catch(() => setHistorique(null));
  }, [typeReleve, e?.matricule, semestre, annee_academique]);

  const fmtDate = (iso: string | null) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <div className="rn-page-wrapper" style={{
      fontFamily: '"Calibri", "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif',
      fontSize: '11px',
      color: COLORS.text,
      background: '#fff',
      letterSpacing: '0.01em',
    }}>
      {/* CSS impression : on isole .rn-page-wrapper en cachant tout le reste */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          /* Tout cacher (backoffice header, sidebar, modal frame, etc.) */
          body * { visibility: hidden !important; }
          /* Sauf le relevé et son contenu */
          .rn-page-wrapper, .rn-page-wrapper * { visibility: visible !important; }
          .rn-page-wrapper {
            position: absolute !important;
            left: 0 !important; top: 0 !important;
            width: 100% !important;
            margin: 0 !important; padding: 0 !important;
            background: #fff !important;
            box-shadow: none !important;
          }
          .rn-no-print { display: none !important; }
          html, body { margin: 0 !important; padding: 0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; background: #fff !important; }
          @page { size: A4 portrait; margin: 0; }
          .rn-page { padding: 0.35cm 0.9cm 0.35cm !important; max-width: none !important; box-shadow: none !important; border-radius: 0 !important; }
        }
        @media print {
          /* Fix 2026-09-14 : impression par le navigateur (Ctrl+P). Les conteneurs de la fenêtre
             d'aperçu (fixe, hauteur limitée, défilement) coupaient le bas du relevé : ils
             deviennent statiques et tout ce qui n'est pas le relevé sort du flux. */
          *:has(.rn-page-wrapper) { position: static !important; overflow: visible !important; max-height: none !important; height: auto !important; transform: none !important; }
          body *:not(:has(.rn-page-wrapper)):not(.rn-page-wrapper):not(.rn-page-wrapper *) { display: none !important; }
          .rn-page-wrapper tr { break-inside: avoid; page-break-inside: avoid; }
        }
        @media screen {
          .rn-page { padding: 14px 28px; max-width: 820px; margin: 0 auto; box-shadow: 0 4px 16px rgba(0,0,0,0.12); border-radius: 4px; }
        }
      ` }} />

      <div className="rn-page" ref={pageRef}>

        {/* FILIGRANE pour relevé provisoire */}
        {isProvisoire && (
          <div className="rn-filigrane" style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%,-50%) rotate(-30deg)',
            fontSize: '140px', fontWeight: 900, opacity: 0.07,
            color: '#DC2626', zIndex: 0, pointerEvents: 'none', userSelect: 'none',
            whiteSpace: 'nowrap', letterSpacing: '12px',
          }}>
            PROVISOIRE
          </div>
        )}

        <div style={{ position: 'relative', zIndex: 1 }}>
          {(data as any).session_annulee && (
            <div style={{
              border: '1.5px solid #B91C1C', background: '#FEF2F2', color: '#7F1D1D',
              padding: '6px 10px', margin: '0 0 6px', fontSize: '11px', lineHeight: 1.4,
              textAlign: 'center', fontWeight: 600,
            }}>
              SESSION ORDINAIRE ANNULÉE — sanction disciplinaire. Les notes d'examen de la session
              ordinaire ne sont pas retenues ; seules comptent les notes de devoirs et de rattrapage.
            </div>
          )}

          {/* ═══ EN-TÊTE ═══ */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '4px' }}>
            <tbody><tr>
              <td style={{ width: '55%', verticalAlign: 'top', paddingRight: '8px' }}>
                <div style={{
                  fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', lineHeight: 1.35,
                  color: C.primary, letterSpacing: '0.03em',
                }}>
                  MINISTÈRE DE L'ENSEIGNEMENT SUPERIEUR
                </div>
                <div style={{
                  background: C.primary, color: '#fff', fontWeight: 700, fontSize: '11px',
                  padding: '3px 8px', margin: '3px 0', display: 'inline-block', borderRadius: '3px',
                  letterSpacing: '0.03em',
                }}>
                  INSTITUT INTERNATIONAL POLYTECHNIQUE &amp; COMMERCE 2I
                </div>
                <div style={{ fontSize: '9px', lineHeight: 1.35, color: COLORS.textMuted }}>
                  <div><span style={{ color: COLORS.text, fontWeight: 600 }}>Tél. :</span> {infoTel}</div>
                  <div><span style={{ color: COLORS.text, fontWeight: 600 }}>E-mail :</span> {infoEmail}</div>
                  <div><span style={{ color: COLORS.text, fontWeight: 600 }}>Adresse :</span> {infoAdresse}</div>
                  <div style={{ paddingLeft: '60px' }}><span style={{ color: COLORS.text, fontWeight: 600 }}>Ville :</span> {infoVille}</div>
                  <div><span style={{ color: COLORS.text, fontWeight: 600 }}>Site :</span> {infoSite}</div>
                  <div style={{ marginTop: '2px', fontStyle: 'italic', fontWeight: 600, color: C.primary }}>{infoAgrement}</div>
                </div>
              </td>
              <td style={{ width: '25%', textAlign: 'center', verticalAlign: 'middle' }}>
                <div style={{
                  background: '#fff',
                  border: `2px solid ${C.primary}`,
                  fontWeight: 800, fontSize: '14px',
                  color: C.primary,
                  padding: '6px 12px',
                  display: 'inline-block',
                  borderRadius: '4px',
                  letterSpacing: '0.06em',
                }}>
                  RELEVÉ DE NOTES
                </div>
                <div style={{
                  background: C.primary, color: '#fff', fontWeight: 700, fontSize: '11px',
                  padding: '3px 14px', marginTop: '6px', display: 'inline-block',
                  borderRadius: '12px', letterSpacing: '0.05em',
                }}>
                  Semestre {semCum}
                </div>
              </td>
              <td style={{ width: '20%', textAlign: 'center', verticalAlign: 'top' }}>
                <div style={{ fontWeight: 700, fontSize: '9px', color: C.primary, letterSpacing: '0.04em' }}>
                  RÉPUBLIQUE DU<br />CONGO
                </div>
                <div style={{ fontSize: '8.5px', fontStyle: 'italic', color: COLORS.textMuted, marginBottom: '2px' }}>
                  Unité-Travail-Progrès
                </div>
                {estDD ? (
                  // Co-marquage Double Diplôme : logo du partenaire ISPI à côté du sceau 2i.
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <img src="/ispi-logo.jpg" alt="ISPI"
                      style={{ height: '30px', maxWidth: '82px', objectFit: 'contain' }} />
                    <img src="/logo.png" alt="Logo 2I"
                      style={{ width: '62px', height: '62px', objectFit: 'contain' }} />
                  </div>
                ) : (
                  <img src="/logo.png" alt="Logo 2I"
                    style={{ display: 'block', margin: '0 auto', width: '80px', height: '80px', objectFit: 'contain' }} />
                )}
                <div style={{ fontSize: '8px', fontStyle: 'italic', fontWeight: 600, color: C.primary, marginTop: '2px' }}>
                  S'instruire pour mieux Servir
                </div>
              </td>
            </tr></tbody>
          </table>

          <div style={{
            height: '2px', background: `linear-gradient(90deg, ${C.primary} 0%, ${C.primaryLight} 50%, ${C.primary} 100%)`,
            marginBottom: '5px', borderRadius: '2px',
          }} />

          {/* ═══ ANNÉE / CYCLE ═══ */}
          <table style={{
            width: '100%', borderCollapse: 'collapse', marginBottom: '3px',
            border: `1px solid ${COLORS.borderLight}`, borderRadius: '3px', overflow: 'hidden',
          }}>
            <tbody><tr>
              <td style={{
                padding: '4px 12px', borderRight: bSoft, width: '50%', fontSize: '12.5px',
                background: C.primarySoft, color: C.primary,
              }}>
                Année Académique <strong style={{ marginLeft: 4 }}>: {annee_academique}</strong>
              </td>
              <td style={{
                padding: '4px 12px', width: '50%', fontSize: '12.5px',
                background: C.primarySoft, color: C.primary,
              }}>
                Cycle <strong style={{ marginLeft: 4 }}>: {e.cycle}</strong>
              </td>
            </tr></tbody>
          </table>

          {/* ═══ INFOS ÉTUDIANT + Photo/QR ═══ */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '4px' }}>
            <tbody><tr>
              <td style={{ verticalAlign: 'top' }}>
                {/* Largeur fixe colonne label : aligne tous les ':' verticalement */}
                <table style={{ borderCollapse: 'collapse', fontSize: '13px', lineHeight: 1.4, textAlign: 'left' }}>
                  <tbody>
                    <tr>
                      <td style={{ width: '90px', color: COLORS.textMuted, textAlign: 'left' }}>Matricule</td>
                      <td style={{ width: '12px', color: COLORS.textMuted }}>:</td>
                      <td style={{ fontWeight: 700, color: COLORS.text, textAlign: 'left' }}>{e.matricule}</td>
                    </tr>
                    <tr>
                      <td style={{ color: COLORS.textMuted, textAlign: 'left' }}>Noms</td>
                      <td style={{ color: COLORS.textMuted }}>:</td>
                      <td style={{ fontWeight: 700, color: COLORS.text, textAlign: 'left' }}>{e.nom.toUpperCase()}</td>
                    </tr>
                    <tr>
                      <td style={{ color: COLORS.textMuted, textAlign: 'left' }}>Prénoms</td>
                      <td style={{ color: COLORS.textMuted }}>:</td>
                      <td style={{ fontWeight: 700, color: COLORS.text, textAlign: 'left' }}>{e.prenoms}</td>
                    </tr>
                    {(e.date_naissance || e.lieu_naissance) && (
                      <tr>
                        <td style={{ whiteSpace: 'nowrap', color: COLORS.textMuted, textAlign: 'left' }}>Date Nce</td>
                        <td style={{ color: COLORS.textMuted }}>:</td>
                        <td style={{ fontWeight: 700, color: COLORS.text, textAlign: 'left' }}>
                          {e.date_naissance
                            ? `${e.date_naissance}${e.lieu_naissance ? ` à ${e.lieu_naissance}` : ''}`
                            : e.lieu_naissance}
                        </td>
                      </tr>
                    )}
                    {/* Niveau + Option sur une seule ligne pour gagner de la place */}
                    <tr>
                      <td style={{ color: COLORS.textMuted, textAlign: 'left' }}>Niveau</td>
                      <td style={{ color: COLORS.textMuted }}>:</td>
                      <td style={{ fontWeight: 700, color: COLORS.text, textAlign: 'left' }}>
                        {e.classe}
                        {e.option && (
                          <>
                            <span style={{ color: COLORS.textMuted, fontWeight: 400, margin: '0 6px' }}>·</span>
                            <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>Option :</span>{' '}
                            <span style={{ color: COLORS.text, fontWeight: 700 }}>{e.option}</span>
                          </>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
              <td style={{ width: '230px', verticalAlign: 'top', textAlign: 'right' }}>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                  {typeReleve === 'definitif' && (
                    qrCodeDataUrl ? (
                      <img src={qrCodeDataUrl} alt="QR" style={{
                        width: '105px', height: '105px', border: `1px solid ${COLORS.borderLight}`, borderRadius: '2px',
                      }} />
                    ) : (
                      <div style={{
                        width: '105px', height: '105px', border: `1px dashed ${COLORS.borderLight}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '8px', color: COLORS.borderLight, textAlign: 'center', borderRadius: '2px',
                      }}>QR Code</div>
                    )
                  )}
                  {e.photo_url ? (
                    <img src={e.photo_url} alt="Photo" style={{
                      width: '105px', height: '105px', objectFit: 'cover',
                      border: `1px solid ${COLORS.borderLight}`, borderRadius: '2px',
                    }} />
                  ) : (
                    <div style={{
                      width: '105px', height: '105px', border: `1px dashed ${COLORS.borderLight}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '8px', color: COLORS.borderLight, borderRadius: '2px',
                    }}>Photo</div>
                  )}
                </div>
                {typeReleve === 'definitif' && numeroReference && (
                  <div style={{
                    fontSize: '9.5px', fontFamily: '"Consolas", "Courier New", monospace',
                    marginTop: '5px', textAlign: 'center',
                    color: '#000', fontWeight: 700,
                    background: '#FEF3C7',
                    border: '1px solid #F59E0B',
                    padding: '4px 8px', borderRadius: '4px',
                    width: '218px',                 /* aligne avec QR(105)+gap(8)+photo(105) */
                    marginLeft: 'auto', marginRight: '0',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    boxSizing: 'border-box',
                  }}>{numeroReference}</div>
                )}
              </td>
            </tr></tbody>
          </table>

          {/* ═══ TABLEAU DES NOTES ═══
              Notes lisibles : 10.5–11 px. Padding vertical réduit pour gagner
              de la place quand il y a beaucoup de matières. */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5px' }}>
            <thead>
              <tr style={{ background: C.primary, color: '#fff' }}>
                <th style={{ borderTop: b, borderBottom: b, borderRight: '1px solid #fff', padding: '4px 6px', textAlign: 'left', fontWeight: 800, fontSize: '11px', width: '28%', letterSpacing: '0.04em' }}>Modules / U.E</th>
                <th style={{ borderTop: b, borderBottom: b, borderRight: '1px solid #fff', padding: '4px 2px', textAlign: 'center', fontWeight: 800, fontSize: '11px', width: '6%' }}>D.1</th>
                <th style={{ borderTop: b, borderBottom: b, borderRight: '1px solid #fff', padding: '4px 2px', textAlign: 'center', fontWeight: 800, fontSize: '11px', width: '6%' }}>D.2</th>
                <th style={{ borderTop: b, borderBottom: b, borderRight: '1px solid #fff', padding: '4px 2px', textAlign: 'center', fontWeight: 800, fontSize: '11px', width: '8%' }}>Moy.D<br />(40%)</th>
                <th style={{ borderTop: b, borderBottom: b, borderRight: '1px solid #fff', padding: '4px 2px', textAlign: 'center', fontWeight: 800, fontSize: '11px', width: '7%' }}>Examen</th>
                <th style={{ borderTop: b, borderBottom: b, borderRight: '1px solid #fff', padding: '4px 2px', textAlign: 'center', fontWeight: 800, fontSize: '11px', width: '8%' }}>Moy.Ex<br />(60%)</th>
                <th style={{ borderTop: b, borderBottom: b, borderRight: '1px solid #fff', padding: '4px 2px', textAlign: 'center', fontWeight: 800, fontSize: '11px', width: '7%' }}>Crédits</th>
                <th style={{ borderTop: b, borderBottom: b, borderRight: '1px solid #fff', padding: '4px 2px', textAlign: 'center', fontWeight: 800, fontSize: '11px', width: '8%' }}>Moy.<br />ECUE</th>
                <th style={{ borderTop: b, borderBottom: b, padding: '4px 2px', textAlign: 'center', fontWeight: 800, fontSize: '11px', width: '8%' }}>Décision</th>
              </tr>
            </thead>
            <tbody>
              {ues.map((ue, ueIdx) => {
                // Fix 2026-09-10 : afficher UNIQUEMENT le nom_ue (qui contient
                // déjà « UE1 …, UE2 … »). Ne plus préfixer par le code
                // technique (UE-L1S1-…-DPr) pour éviter la double mention.
                // Si le nom ne commence pas par « UE » (rare), on préfixe
                // par « UE<idx> » pour rester lisible.
                const nomAff = (ue.nom_ue || '').trim();
                const labelUE = /^UE\s?\d/i.test(nomAff)
                  ? nomAff
                  : `UE${ueIdx + 1}${nomAff ? ' ' + nomAff : ''}`;
                return (
                  <React.Fragment key={ue.ue_id}>
                    {/* Lignes ECUE (matières) D'ABORD — toutes blanches.
                        La cellule Décision est portée par la 1ère matière avec
                        rowSpan = nb_matieres : couvre TOUTES les matières (mais
                        PAS la ligne UE qui a sa propre cellule Décision). */}
                    {ue.matieres.map((mat, matIdx) => {
                      const mD = mat.moy_devoirs != null ? mat.moy_devoirs * 0.4 : null;
                      const mE = mat.note_examen != null ? mat.note_examen * 0.6 : null;
                      return (
                        <tr key={mat.matiere_id} style={{ background: '#fff' }}>
                          <td style={{ border: b, padding: '3px 4px 3px 12px', fontSize: '10.5px', textAlign: 'left' }}>{mat.nom_matiere}</td>
                          <td style={{ border: b, textAlign: 'center', fontSize: '11px', padding: '3px 2px' }}>
                            {mat.absent_d1 ? <span style={{ color: '#C00', fontWeight: 600 }}>AB</span> : fN(mat.note_d1)}
                          </td>
                          <td style={{ border: b, textAlign: 'center', fontSize: '11px', padding: '3px 2px' }}>
                            {mat.absent_d2 ? <span style={{ color: '#C00', fontWeight: 600 }}>AB</span> : fN(mat.note_d2)}
                          </td>
                          <td style={{ border: b, textAlign: 'center', fontSize: '11px', padding: '3px 2px', color: COLORS.textMuted }}>
                            {(mat.absent_d1 && mat.absent_d2)
                              ? <span style={{ color: '#C00', fontWeight: 600 }}>AB</span>
                              : fN(mD)}
                          </td>
                          <td style={{ border: b, textAlign: 'center', fontSize: '11px', padding: '3px 2px' }}>
                            {mat.absent_examen
                              ? <span style={{ color: '#C00', fontWeight: 600 }}>AB</span>
                              : <>{fN(mat.note_examen)}{mat.est_rattrapage && <span style={{ color: '#D97706', fontSize: '8px' }}> (R)</span>}</>}
                          </td>
                          <td style={{ border: b, textAlign: 'center', fontSize: '11px', padding: '3px 2px', color: COLORS.textMuted }}>
                            {mat.absent_examen ? <span style={{ color: '#C00', fontWeight: 600 }}>AB</span> : fN(mE)}
                          </td>
                          <td style={{ border: b, textAlign: 'center', fontSize: '11px', padding: '3px 2px' }}>{mat.credits}</td>
                          <td style={{ border: b, textAlign: 'center', fontSize: '11.5px', padding: '3px 2px', fontWeight: 700 }}>{fN(mat.moyenne_ecue)}</td>
                          {matIdx === 0 && (
                            <td rowSpan={ue.matieres.length} style={{ border: b, background: '#fff' }} />
                          )}
                        </tr>
                      );
                    })}
                    {/* Ligne UE EN BAS — padding vertical réduit pour gagner de la place */}
                    <tr style={{ background: C.primarySoft }}>
                      <td colSpan={6} style={{ border: b, padding: '3px 8px', fontWeight: 700, fontSize: '10.5px', color: C.primary, textAlign: 'left' }}>
                        {labelUE}
                      </td>
                      <td style={{ border: b, textAlign: 'center', fontWeight: 700, fontSize: '10.5px', padding: '3px 2px', color: C.primary }}>
                        {ue.credits_total}
                      </td>
                      <td style={{ border: b, textAlign: 'center', fontWeight: 700, fontSize: '11px', padding: '3px 2px', color: C.primary }}>
                        {fN(ue.moyenne_ue)}
                      </td>
                      <td style={{
                        border: b, textAlign: 'center', fontWeight: 700, fontSize: '10px', padding: '3px 2px',
                        color: ue.validee ? COLORS.validText : COLORS.invalidText,
                        background: ue.validee ? COLORS.validBg : COLORS.invalidBg,
                        lineHeight: 1.2,
                      }}>
                        {ue.validee ? (ue.validee_par_compensation ? 'Validé (comp.)' : 'Validé') : 'Non validé'}
                        {ue.validee && ue.session_validation === 'rattrapage' && (
                          <div style={{ color: '#D97706', fontSize: '8px', fontWeight: 600, marginTop: '1px' }}>(rattrapage)</div>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}

              {/* Ligne MOYENNE GENERALE */}
              <tr style={{ background: C.primarySoft, color: COLORS.text }}>
                <td colSpan={6} style={{ border: b, padding: '4px 8px', fontWeight: 800, fontSize: '11.5px', letterSpacing: '0.04em', textAlign: 'left' }}>
                  {dd ? 'MOYENNE FORMATION INITIALE' : 'MOYENNE GENERALE'}
                </td>
                <td style={{ border: b, textAlign: 'center', fontWeight: 800, fontSize: '11.5px', padding: '4px 2px' }}>
                  {rs ? fN(rs.credits_obtenus) : '—'}
                </td>
                <td style={{ border: b, textAlign: 'center', fontWeight: 800, fontSize: '13px', padding: '4px 2px' }}>
                  {fN(rs?.moyenne_semestre ?? null)}
                </td>
                <td style={{ border: b, textAlign: 'center', fontWeight: 800, fontSize: '10.5px', padding: '4px 2px' }}>
                  {rs?.valide ? 'Admis' : 'Ajourné'}
                </td>
              </tr>
            </tbody>
          </table>

          {/* ═══ CURSUS DOUBLE DIPLÔME (étudiants DD, semestre pair) ═══ */}
          {dd && dd.modules.length > 0 && (
            <div style={{ marginTop: '6px' }}>
              <div style={{
                background: C.primary, color: '#fff', fontWeight: 800, fontSize: '11px',
                letterSpacing: '0.05em', padding: '3px 8px', borderRadius: '3px 3px 0 0', textAlign: 'center',
              }}>
                CURSUS DOUBLE DIPLÔME
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                <thead>
                  <tr style={{ background: C.primarySoft, color: COLORS.text }}>
                    <th style={{ border: b, padding: '3px 6px', textAlign: 'left', fontWeight: 700 }}>Module</th>
                    <th style={{ border: b, padding: '3px 6px', textAlign: 'center', fontWeight: 700, width: '80px' }}>Note / 20</th>
                    <th style={{ border: b, padding: '3px 6px', textAlign: 'center', fontWeight: 700, width: '110px' }}>Appréciation</th>
                  </tr>
                </thead>
                <tbody>
                  {dd.modules.map((m, i) => (
                    <tr key={i}>
                      <td style={{ border: b, padding: '2px 6px', textAlign: 'left' }}>{m.module}</td>
                      <td style={{ border: b, padding: '2px 6px', textAlign: 'center', fontWeight: 700, color: C.primary }}>
                        {fN(m.note)}
                      </td>
                      <td style={{ border: b, padding: '2px 6px', textAlign: 'center', color: COLORS.textMuted, fontWeight: 600 }}>
                        {apprecDD(m.note)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: C.primarySoft }}>
                    <td style={{ border: b, padding: '3px 6px', textAlign: 'right', fontWeight: 800 }}>
                      MOYENNE DOUBLE DIPLÔME
                    </td>
                    <td style={{ border: b, padding: '3px 6px', textAlign: 'center', fontWeight: 800, fontSize: '12px' }}>
                      {fN(dd.moyenne_dd)}
                    </td>
                    <td style={{ border: b, padding: '3px 6px', textAlign: 'center', fontWeight: 800 }}>
                      {apprecDD(dd.moyenne_dd)}
                    </td>
                  </tr>
                </tbody>
              </table>
              {/* Bandeau 3 moyennes */}
              <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                {[
                  { l: 'MOY. FORMATION INITIALE', v: dd.moyenne_fi },
                  { l: 'MOY. DOUBLE DIPLÔME', v: dd.moyenne_dd },
                  { l: 'MOYENNE GÉNÉRALE (FI + DD)', v: dd.moyenne_generale, fort: true },
                ].map((c, i) => (
                  <div key={i} style={{
                    flex: 1, textAlign: 'center', padding: '4px 2px', borderRadius: '4px',
                    border: `1.5px solid ${C.primary}`,
                    background: c.fort ? C.primary : '#fff',
                    color: c.fort ? '#fff' : COLORS.text,
                  }}>
                    <div style={{ fontSize: '8px', fontWeight: 700, letterSpacing: '0.03em' }}>{c.l}</div>
                    <div style={{ fontSize: '14px', fontWeight: 800 }}>{fN(c.v ?? null)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ RÉSUMÉ SEMESTRIEL (labels sur fond bleu, valeurs encadrées contour bleu arrondi) ═══ */}
          <table style={{
            width: '100%', borderCollapse: 'collapse', marginTop: '3px', fontSize: '10.5px',
            border: `1.5px solid ${C.primary}`, borderRadius: '4px', overflow: 'hidden',
          }}>
            <tbody>
              <tr>
                <td style={{
                  borderRight: bSoft, borderBottom: bSoft, padding: '4px 8px', width: '30%',
                  background: '#fff', color: '#000', fontWeight: 800, letterSpacing: '0.03em', textAlign: 'left',
                }}>{dd ? 'MOYENNE FORMATION INITIALE' : 'MOYENNE GENERALE'}</td>
                <td style={{
                  borderRight: bSoft, borderBottom: bSoft, padding: '2px 8px', width: '20%',
                  background: '#fff', textAlign: 'center',
                }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 12px', minWidth: '60px',
                    border: `1.5px solid ${C.primary}`, borderRadius: '8px',
                    fontWeight: 800, fontSize: '14px', color: '#000',
                  }}>{fN(rs?.moyenne_semestre ?? null)}</span>
                </td>
                <td style={{
                  borderRight: bSoft, borderBottom: bSoft, padding: '4px 8px', width: '15%',
                  background: '#fff', color: '#000', fontWeight: 800, letterSpacing: '0.03em', textAlign: 'left',
                }}>RANG</td>
                <td style={{
                  borderBottom: bSoft, padding: '2px 8px', width: '35%',
                  background: '#fff', textAlign: 'center',
                }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 12px', minWidth: '60px',
                    border: `1.5px solid ${C.primary}`, borderRadius: '8px',
                    fontWeight: 800, fontSize: '13px', color: '#000',
                  }}>
                    {rs?.rang != null
                      ? <>{ordinal(rs.rang)}{rs.effectif_classe ? <span style={{ fontSize: '10px', fontWeight: 500, color: COLORS.textMuted }}> / {rs.effectif_classe}</span> : null}</>
                      : '—'}
                  </span>
                </td>
              </tr>
              <tr>
                <td style={{
                  borderRight: bSoft, borderBottom: bSoft, padding: '3px 8px',
                  background: '#fff', color: '#000', fontWeight: 800, letterSpacing: '0.03em', textAlign: 'left',
                }}>NOMBRE DE CRÉDITS</td>
                <td colSpan={3} style={{
                  borderBottom: bSoft, padding: '2px 8px',
                  background: '#fff', textAlign: 'center',
                }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 12px', minWidth: '120px',
                    border: `1.5px solid ${C.primary}`, borderRadius: '8px',
                    fontWeight: 800, fontSize: '13px', color: '#000',
                  }}>{rs ? fN(rs.credits_obtenus) : '—'} / {rs ? rs.credits_requis : 30}</span>
                </td>
              </tr>
              <tr>
                <td style={{
                  borderRight: bSoft, borderBottom: bSoft, padding: '3px 8px',
                  background: '#fff', color: '#000', fontWeight: 800, letterSpacing: '0.03em', textAlign: 'left',
                }}>MENTION</td>
                <td colSpan={3} style={{
                  borderBottom: bSoft, padding: '2px 8px',
                  background: '#fff', textAlign: 'center',
                }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 12px', minWidth: '120px',
                    border: `1.5px solid ${C.primary}`, borderRadius: '8px',
                    fontWeight: 800, fontSize: '12px', color: '#000',
                  }}>{mention}</span>
                </td>
              </tr>
              <tr>
                <td style={{
                  borderRight: bSoft, padding: '4px 8px',
                  background: '#fff', color: '#000', fontWeight: 800, letterSpacing: '0.03em', textAlign: 'left',
                }}>DÉCISION</td>
                {(() => {
                  const validApresRattrap = rs?.valide && rs.session_validation === 'rattrapage';
                  // Couleur ambrée pour validation après rattrapage (différencie de SO)
                  const ratrapBg = '#FEF3C7';  // amber-100
                  const ratrapText = '#92400E'; // amber-800
                  const bg = !rs?.valide
                    ? COLORS.invalidBg
                    : (validApresRattrap ? ratrapBg : COLORS.validBg);
                  const txt = !rs?.valide
                    ? COLORS.invalidText
                    : (validApresRattrap ? ratrapText : COLORS.validText);
                  return (
                    <td style={{
                      borderRight: bSoft, padding: '4px 8px', fontWeight: 800, fontSize: '12px', textAlign: 'center',
                      background: bg, color: txt,
                    }}>
                      {rs?.valide ? 'SEMESTRE VALIDÉ' : 'AJOURNÉ'}
                      {rs?.valide && rs.session_validation === 'normale' && (
                        <div style={{ fontSize: '8.5px', fontWeight: 600, marginTop: '1px', opacity: 0.85 }}>
                          (1ère session)
                        </div>
                      )}
                      {validApresRattrap && (
                        <div style={{ fontSize: '8.5px', fontWeight: 700, marginTop: '1px' }}>
                          (après rattrapage)
                        </div>
                      )}
                    </td>
                  );
                })()}
                <td colSpan={2} style={{ padding: '3px 8px', fontSize: '10px', background: '#fff', textAlign: 'center' }}>
                  Fait à {infoVilleCap}, le <strong>{dateStr}</strong>
                  <div style={{ marginTop: '3px', fontWeight: 700, fontSize: '10.5px' }}>{chefTitre}</div>
                  {typeReleve !== 'definitif' && (
                    <div style={{ fontSize: '8.5px', color: COLORS.textMuted, fontStyle: 'italic' }}>(Signature et Cachet)</div>
                  )}
                  {isBZ && chefNom && (
                    <div style={{ marginTop: '24px', fontWeight: 700, fontSize: '10.5px' }}>{chefNom}</div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Mention provisoire */}
          {isProvisoire && (
            <div style={{
              marginTop: '10px', padding: '6px 10px',
              background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '3px',
              fontSize: '9px', color: '#78350F', fontStyle: 'italic', textAlign: 'center',
            }}>
              Ce relevé est <strong>PROVISOIRE</strong> et susceptible d'être modifié après délibération définitive du jury.
            </div>
          )}

          {/* Boutons (écran seulement) */}
          {/* ALERTE HISTORIQUE (def. seulement) : visible à l'écran, masquée à l'impression */}
          {typeReleve === 'definitif' && historique && historique.deja_imprime_definitif && (
            <div className="rn-no-print" style={{
              marginTop: '14px',
              padding: '10px 14px',
              background: '#FEF3C7',
              border: '1.5px solid #F59E0B',
              borderRadius: '6px',
              fontSize: '12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div>
                  <strong style={{ color: '#92400E' }}>⚠ Relevé définitif déjà imprimé</strong>
                  <div style={{ marginTop: '4px', color: '#78350F' }}>
                    {historique.nombre_impressions_definitif} copie(s) délivrée(s) en {historique.nombre_evenements_definitif} impression(s) ·
                    {' '}dernière : <strong>{fmtDate(historique.derniere_impression_definitif)}</strong>
                  </div>
                </div>
                <button
                  onClick={() => setShowHistoriqueDetails((v) => !v)}
                  style={{
                    padding: '4px 10px', background: '#F59E0B', color: '#fff',
                    border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 600,
                  }}
                >
                  {showHistoriqueDetails ? 'Masquer' : 'Voir détails'}
                </button>
              </div>
              {showHistoriqueDetails && (
                <table style={{ width: '100%', marginTop: '10px', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead>
                    <tr style={{ background: '#FBBF24', color: '#fff' }}>
                      <th style={{ padding: '4px 6px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '4px 6px', textAlign: 'center' }}>Copies</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left' }}>Imprimé par</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left' }}>N° référence</th>
                      <th style={{ padding: '4px 6px', textAlign: 'left' }}>Concerne</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historique.impressions.map((imp) => (
                      <tr key={imp.id} style={{ background: imp.type_releve === 'definitif' ? '#FFFBEB' : '#fff' }}>
                        <td style={{ padding: '3px 6px', border: '1px solid #FDE68A' }}>{fmtDate(imp.date_impression)}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #FDE68A', fontWeight: imp.type_releve === 'definitif' ? 700 : 400 }}>
                          {imp.type_releve === 'definitif' ? 'DÉFINITIF' : 'Provisoire'}
                        </td>
                        <td style={{ padding: '3px 6px', border: '1px solid #FDE68A', textAlign: 'center' }}>{imp.nombre_copies}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #FDE68A' }}>{imp.imprime_par_nom}{imp.imprime_par_role && <span style={{ color: '#78350F', fontSize: '10px' }}> ({imp.imprime_par_role})</span>}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #FDE68A', fontFamily: 'monospace', fontSize: '10px' }}>{imp.numero_reference_externe || '—'}</td>
                        <td style={{ padding: '3px 6px', border: '1px solid #FDE68A', fontSize: '10px' }}>{imp.concerne}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="rn-no-print" style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '20px', paddingBottom: '12px' }}>
            <button
              onClick={imprimer}
              disabled={impressionEnCours}
              style={{
                padding: '9px 24px', background: C.primary, color: '#fff', border: 'none',
                borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                letterSpacing: '0.03em',
              }}
            >
              {impressionEnCours ? 'Préparation…' : 'Imprimer'}
            </button>
            {onClose && (
              <button
                onClick={onClose}
                style={{
                  padding: '9px 24px', background: '#fff', color: COLORS.text,
                  border: `1px solid ${COLORS.borderLight}`, borderRadius: '6px',
                  cursor: 'pointer', fontSize: '13px',
                }}
              >
                Fermer
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default RelevePreview;
